import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Clock, Eye, PenLine, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  buildAntiDrugAnnexurePdf,
  buildAntiDrugPolicyPdf,
  buildHighRiskConsentPdf,
} from "@/lib/affidavit";
import {
  signPolicyDigitally,
  acknowledgePolicy,
  fetchPolicySignSigners,
} from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// India campus app — calendar dates must follow Asia/Kolkata, not UTC.
// `toISOString().slice(0, 10)` is wrong after ~5:30 AM IST previous day /
// before 5:30 AM IST (shows yesterday/tomorrow on the signed PDF).
const IST_TZ = "Asia/Kolkata";

const todayISO = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
};

// Parse "YYYY-MM-DD" as a calendar date (not UTC midnight) to avoid off-by-one
// when formatting for en-IN / IST.
const parseISODateLocal = (iso) => {
  if (!iso) return null;
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const ORDINAL_SUFFIX = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Decompose an ISO date into the "day / month / year" parts used by the
// affidavit's "Declared this ___ day of ___ month of ___ year ___" phrasing.
const declaredDateParts = (iso) => {
  const d = parseISODateLocal(iso);
  if (!d) return { day: "____", month: "____", year: "____" };
  return {
    day: ORDINAL_SUFFIX(d.getDate()),
    month: d.toLocaleString("en-US", { month: "long" }),
    year: String(d.getFullYear()),
  };
};

// Human-readable "9 July 2026" form, for simple single-line date labels.
const formatDate = (iso) => {
  const d = parseISODateLocal(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result || "";
      resolve(String(dataUrl).split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Finds the reason for the most recent "reupload requested" event that targeted
// this exact policy key — the app-level lastActionReason is shared across all
// docs/policies, so it can show the wrong reason once more than one item has been flagged.
const reuploadReasonFor = (history, key) => {
  const entry = (history || []).find((h) => h?.detail && h.detail[key] === "reupload");
  return entry?.reason || null;
};

const BUILDERS = {
  antiDrugAnnexure: buildAntiDrugAnnexurePdf,
  antiDrugPolicy: buildAntiDrugPolicyPdf,
  highRiskConsent: buildHighRiskConsentPdf,
};

// Preview panel height sized per policy — Anti-Drug Annexure has two full
// affidavits' worth of text (student + parent sections), so it gets much
// more room than the shorter single-page policies.
const PREVIEW_PANEL_MAX_HEIGHT = {
  antiDrugAnnexure: "max-h-[760px]",
  antiDrugPolicy: "max-h-[420px]",
  highRiskConsent: "max-h-[480px]",
};

// Whole years between a "YYYY-MM-DD" date of birth and today.
const calculateAge = (dobISO) => {
  if (!dobISO) return "";
  const dob = new Date(dobISO);
  if (Number.isNaN(dob.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthdayThisYear = today.getMonth() < dob.getMonth()
    || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthdayThisYear) age -= 1;
  return age >= 0 ? String(age) : "";
};

// Resolve auto-source path like "candidate.name", "candidate.fullAddress", "mobile", "applicationNumber", "today"
const resolveAuto = (autoPath, state) => {
  if (!autoPath) return "";
  if (autoPath === "today") return todayISO();
  if (autoPath === "mobile") return state.mobile || "";
  if (autoPath === "applicationNumber") return state.applicationNumber || "";
  if (autoPath === "candidate.fullAddress") {
    return [state.candidate?.address, state.candidate?.city, state.candidate?.state, state.candidate?.pincode]
      .filter(Boolean).join(", ");
  }
  if (autoPath === "candidate.age") return calculateAge(state.candidate?.dateOfBirth);
  if (autoPath === "courseInfo.program") {
    // Prefer program name; fall back to display/branch. Never invent "BBA".
    return (
      state.courseInfo?.program
      || state.courseInfo?.display
      || state.courseInfo?.branch
      || ""
    );
  }
  if (autoPath === "courseInfo.display") return state.courseInfo?.display || "";
  if (autoPath.startsWith("candidate.")) {
    const k = autoPath.replace("candidate.", "");
    return state.candidate?.[k] || "";
  }
  return "";
};

const Highlight = ({ v, ph }) => (
  <span className={`px-1 rounded ${v ? "bg-yellow-100 text-[#111] font-semibold" : "bg-red-50 text-red-500 italic"}`}>{v || ph}</span>
);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

// Candidate/parent1/parent2 emails are OTP-verified up front at the Contact &
// Parent Details step, so this is display-only context, not a "please verify"
// prompt — there's nothing left to correct here.
const sourceHint = (source) => {
  if (source === "confirmed" || source === "saved") return "Verified at registration";
  if (source === "registration") return "From your registration";
  if (source === "admissions") return "From admissions records";
  return "Confirmed for this signing request";
};

// A single interactive policy form: fill fields → live preview → Send for
// Sign (marks it complete; no file upload — digital signing is a future
// phase). Shared between the registration wizard's Policies step and the
// candidate dashboard's inline reupload flow.
export const PolicyBuilder = ({ item, value, onChange, state }) => {
  const docStatus = state.docStatuses?.[item.id];
  const needsReupload = docStatus === "reupload";
  const reuploadReason = needsReupload ? reuploadReasonFor(state.history, item.id) : null;

  // Merge auto/default values with saved user-entered values. View-only
  // policies (e.g. the real Anti-Drug Policy PDF) have no fields at all.
  // studentGender/parent1Relation are always mirrored in from Candidate
  // Details (not user-editable here) — used to derive "son/daughter",
  // "Mr./Mrs." wording in the Anti-Drug Affidavit and High-Risk Consent text.
  //
  // Locked auto fields always follow the live source of truth (e.g.
  // courseInfo.program). A stale saved value like the old hardcoded "BBA"
  // default must not win over the admissions program once it loads — that
  // mismatch is what made the form show BBA while Leegality got the real
  // program (or vice versa depending on hydration timing).
  const filled = useMemo(() => {
    const out = { ...(value?.filled || {}) };
    (item.fields || []).forEach((f) => {
      const autoVal = resolveAuto(f.auto, state);
      if (f.locked && f.auto) {
        const saved = (out[f.key] || "").toString().trim();
        // Live admissions always wins. Treat a saved "BBA" as the old
        // hardcoded placeholder — clear it until courseInfo loads so we never
        // send BBA to Leegality for a non-BBA student.
        if (autoVal) {
          out[f.key] = autoVal;
        } else if (!saved || saved === "BBA") {
          out[f.key] = f.default || "";
        }
      } else if (out[f.key] === undefined || out[f.key] === "") {
        out[f.key] = autoVal || f.default || "";
      }
    });
    out.studentGender = out.studentGender || state.candidate?.gender || "";
    out.parent1Relation = state.candidate?.parent1Relation || "";
    return out;
  }, [value?.filled, item.fields, state]);

  // signStatus: undefined (not sent) → "pending" (out for digital signature
  // via Leegality) → "signed" (all signers completed). Kept separate from
  // `sent` (still set for backward compatibility with existing records).
  const signStatus = value?.signStatus || (value?.sent ? "pending" : undefined);
  const isPending = signStatus === "pending";
  const isSigned = signStatus === "signed";
  const isFrozen = (isPending || isSigned) && !needsReupload;

  // Persist corrected locked auto values (esp. course) back into policiesFilled
  // so a stale "BBA" doesn't keep getting re-synced to the backend.
  useEffect(() => {
    if (isFrozen) return;
    const saved = value?.filled || {};
    const patch = {};
    (item.fields || []).forEach((f) => {
      if (!(f.locked && f.auto)) return;
      const next = filled[f.key];
      const prev = saved[f.key];
      if (next && next !== prev) patch[f.key] = next;
      if (!next && prev === "BBA") patch[f.key] = "";
    });
    if (Object.keys(patch).length) {
      onChange({ ...(value || {}), filled: { ...saved, ...filled, ...patch } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when filled course/auto sources change
  }, [filled.course, state.courseInfo?.program, state.courseInfo?.display, state.courseInfo?.branch, isFrozen]);

  const setFilled = (partial) => {
    if (isFrozen) return;
    onChange({ ...(value || {}), filled: { ...filled, ...partial } });
  };
  // Parent 2 is optional in Candidate Details — their fields still show on
  // High-Risk Consent (informational) when present, but they're never a
  // required signer, regardless of whether their details were filled in.
  const hasParent2 = !!(state.candidate?.parent2Name || "").trim();
  const signerLabel = item.id === "highRiskConsent" ? "Parent 1 only" : item.signers;
  const optionalKeys = item.id === "highRiskConsent"
    ? ["parent2Name", "parent2Phone"]
    : [...(hasParent2 ? [] : ["parent2Name", "parent2Phone"])];
  const canProceed = (item.fields || [])
    .filter((f) => !optionalKeys.includes(f.key))
    .every((f) => {
      if (f.type === "checkbox") return filled[f.key] === true;
      return (filled[f.key] || "").toString().trim().length > 0;
    });
  const [signing, setSigning] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signersLoading, setSignersLoading] = useState(false);
  const [modalSigners, setModalSigners] = useState([]);
  const [hasViewed, setHasViewed] = useState(!!value?.viewed);
  const [acking, setAcking] = useState(false);

  // Sign-status polling now lives in RegistrationContext (wizard-wide, survives
  // navigating to a later step) instead of here — this component just reads
  // whatever `value.signStatus` the context keeps fresh.

  const doPreview = () => {
    if (!canProceed) return toast.error("Please fill all fields before previewing");
    const { url } = BUILDERS[item.id](filled);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const doSignDigitally = async () => {
    if (!canProceed) return toast.error("Please fill all fields before signing digitally");
    setSigning(true);
    let pdfBase64;
    try {
      const built = BUILDERS[item.id](filled);
      pdfBase64 = await blobToBase64(built.blob);
      URL.revokeObjectURL(built.url);
    } catch (err) {
      console.error("Failed to build signing PDF", err);
    }
    // No signerEmails override — the backend already resolves the right address
    // per signer (OTP-verified at Contact & Parent Details, falling back to
    // admissions records), same values this modal displays for confirmation.
    const { ok, data } = await signPolicyDigitally(
      state.applicationNumber,
      item.id,
      filled,
      pdfBase64,
    );
    setSigning(false);
    if (!ok) {
      toast.error(data?.detail || "Could not send for digital signature");
      return;
    }
    if (data?.configured === false) {
      toast.error("Digital signature service isn't set up yet — contact the admissions office.");
      return;
    }
    onChange({ ...(value || {}), filled, sent: true, signStatus: "pending", leegalityDocumentId: data?.documentId, sentAt: new Date().toISOString() });
    toast.success(`${item.label} sent for digital signature`);
    setSignModalOpen(false);
  };

  const modalEmailsValid = useMemo(() => {
    const requiredInvalid = modalSigners.filter((s) => s.required && !isValidEmail(s.email));
    if (requiredInvalid.length) return false;
    return !modalSigners.some((s) => !s.required && s.email && !isValidEmail(s.email));
  }, [modalSigners]);

  const canSendDigitally = modalEmailsValid && !signersLoading && !signing;

  const openSignModal = async () => {
    if (!canProceed) return toast.error("Please fill all fields before signing digitally");
    setSignModalOpen(true);
    setSignersLoading(true);
    const data = await fetchPolicySignSigners(state.applicationNumber, item.id);
    setSignersLoading(false);
    if (!data?.signers?.length) {
      toast.error("Could not load signing recipients — please try again");
      setSignModalOpen(false);
      return;
    }
    setModalSigners((data.signers || []).map((s) => ({ ...s, email: s.email || s.defaultEmail || "" })));
  };

  const confirmSignFromModal = async () => {
    if (!canSendDigitally) return;
    const invalid = modalSigners.filter((s) => s.required && !isValidEmail(s.email));
    if (invalid.length) {
      toast.error(`${invalid.map((s) => s.label).join(", ")} — no valid email on file. Please contact the admissions office.`);
      return;
    }
    await doSignDigitally();
  };

  // ---- View-only policy (real static PDF + "read and understood" checkbox) ----
  const doViewPolicy = () => {
    window.open(item.pdfUrl, "_blank", "noopener,noreferrer");
    setHasViewed(true);
    onChange({ ...(value || {}), viewed: true });
  };

  const doAcknowledge = async () => {
    setAcking(true);
    const { ok, data } = await acknowledgePolicy(state.applicationNumber, item.id);
    setAcking(false);
    if (!ok) {
      toast.error(data?.detail || "Could not save your acknowledgement — please try again");
      return;
    }
    onChange({ ...(value || {}), viewed: true, sent: true, signStatus: "signed", signedAt: data?.signedAt || new Date().toISOString() });
    toast.success(`${item.label} acknowledged`);
  };

  // ---- Human-readable preview text (paragraph form with highlights) ----
  const H = Highlight;

  const previewText = () => {
    if (item.id === "antiDrugAnnexure") {
      const relationWord = (() => {
        const g = (filled.studentGender || "").trim().toLowerCase();
        if (g === "male") return "son";
        if (g === "female") return "daughter";
        return "son/daughter";
      })();
      const salutation = (() => {
        const r = (filled.parent1Relation || "").trim().toLowerCase();
        if (r === "father") return "Mr.";
        if (r === "mother") return "Mrs.";
        return "Mr./Mrs.";
      })();
      return (
        <>
          <div className="font-semibold text-center">ANTI-DRUG AFFIDAVIT</div>
          <div className="text-center italic text-[#6B7280] text-[11px] mt-1">(For Students Against Drug Use and Possession in Telangana)</div>
          <p className="mt-3">As per the guidelines and instructions provided by the Government of Telangana, Mahindra University follows the following pointers. All its stakeholders must abide by them.</p>
          <p className="mt-2">
            I <H v={filled.studentName} ph="__________" /> (name) {relationWord} of {salutation} <H v={filled.parent1Name} ph="__________" /> (name), Address: <H v={filled.address} ph="__________" />, Phone No: <H v={filled.phoneNumber} ph="__________" /> admitted to <H v={filled.course} ph="__________" />, 2026 in Mahindra University hereby agree to the following terms.
          </p>
          <div className="mt-4 font-semibold text-center">Declaration</div>
          <p className="mt-1 text-[#6B7280]">A full 14-point declaration follows (UGC/NDPS Act awareness, compliance, cooperation with authorities, medical check-ups, and disciplinary consequences) — see the generated PDF for the complete text.</p>
          <div className="mt-4 font-semibold text-center">Affirmation</div>
          <p className="mt-1">I hereby affirm that the above declaration is true and correct to the best of my knowledge and belief. I understand that this affidavit shall remain legally binding throughout my period of study in the institution.</p>
          <div className="mt-4 space-y-1">
            <div>Signature &amp; Name of Student: <H v={filled.studentName} ph="__________" /></div>
            <div>Signature &amp; Name of Parent/Guardian: <H v={filled.parent1Name} ph="__________" /></div>
          </div>
        </>
      );
    }
    if (item.id === "antiDrugPolicy") {
      return (
        <>
          <div className="font-semibold">ANTI DRUG / SUBSTANCE / ALCOHOL USE POLICY</div>
          <p className="mt-2">Mahindra University is committed to a campus environment free from alcohol and substance use. The use, possession or distribution of any &lsquo;substance&rsquo; is banned on the campus, in its facilities and in University functions and programs. Violations attract disciplinary action — including probation, suspension or expulsion — and may involve local law enforcement under the Indian Narcotic Drugs and Psychotropic Substances Act.</p>
          <div className="mt-4 font-semibold">ACKNOWLEDGEMENT</div>
          <p className="mt-1">I, <H v={filled.studentName} ph="__________" /> (Roll / App. No. <H v={filled.rollNo} ph="__________" />), currently pursuing <H v={filled.course} ph="__________" /> at Mahindra University, hereby acknowledge that I have received, read and fully understood the above Policy and agree to abide by it.</p>
          <div className="mt-3">Place : <H v={filled.place} ph="__________" /> · Date : <H v={formatDate(filled.signedOn)} ph="__________" /></div>
          <div className="mt-2">Signature of the Student : ______________________________</div>
        </>
      );
    }
    // highRiskConsent — show one or two parent columns depending on whether
    // Candidate Details actually has a Parent 2 (optional there), and
    // gender-appropriate wording for son/daughter, Mr./Ms., his/her.
    const genderWords = (() => {
      const g = (filled.studentGender || "").trim().toLowerCase();
      if (g === "male") return { child: "son", title: "Mr.", pronoun: "his" };
      if (g === "female") return { child: "daughter", title: "Ms.", pronoun: "her" };
      return { child: "son/daughter", title: "Mr./Ms.", pronoun: "his/her" };
    })();
    return (
      <>
        <div className="font-semibold text-center">HIGH-RISK CONSENT</div>
        <p className="mt-2">
          I, <H v={filled.parent1Name} ph="__________" /> have explained about our {genderWords.child}&rsquo;s condition. {genderWords.title} <H v={filled.studentName} ph="__________" />, <H v={filled.studentAge} ph="__" /> years old <H v={filled.studentGender} ph="male/female" />, studying <H v={filled.course} ph="__________" /> (Branch, Program and School) at MAHINDRA UNIVERSITY who has been diagnosed with <H v={filled.medicalIllness !== "None" ? filled.medicalIllness : ""} ph="__________" />, a medical illness. We give full consent to be residents of the university campus. We don&rsquo;t hold the management {"{or}"} medical personnel responsible if any untoward event happens like worsening of {genderWords.pronoun} condition or over dosage of the prescribed drugs by the family doctor. In case the situation is not resolved and further treatment is required we will take our ward home or as required depending on Mahindra University doctors&rsquo; advice.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 max-w-xs">
          <div>PARENT 1 NAME: <H v={filled.parent1Name} ph="__________" /></div>
          <div>PARENT 1 SIGN: </div>
          <div>PHONE NUMBER: <H v={filled.parent1Phone} ph="__________" /></div>
          <div>DATE: <H v={formatDate(filled.declaredDate)} ph="__________" /></div>
        </div>
      </>
    );
  };

  if (item.viewOnly) {
    return (
      <motion.div layout className="mu-card p-5 md:p-6 space-y-4" data-testid={`policy-card-${item.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-[#111] flex items-center gap-2">
              <PenLine className="h-4 w-4 text-[#B60F2A]" /> {item.label}
            </h3>
            <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">{item.description}</p>
          </div>
          {isSigned ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full shrink-0" data-testid={`policy-badge-signed-${item.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Read
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full shrink-0">
              <Clock className="h-3.5 w-3.5" /> Not Read
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={doViewPolicy} className="mu-btn-secondary text-sm" data-testid={`policy-view-${item.id}`}>
            <Eye className="h-4 w-4" /> View Policy
          </button>
        </div>

        <label className={`flex items-start gap-2 text-sm text-[#111] leading-snug select-none ${!hasViewed || isSigned || acking ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed"
            checked={isSigned}
            disabled={!hasViewed || isSigned || acking}
            onChange={(e) => { if (e.target.checked) doAcknowledge(); }}
            data-testid={`policy-acknowledge-${item.id}`}
          />
          <span>{acking ? "Saving…" : "I have read and understood the policy"}</span>
        </label>

        {!hasViewed && !isSigned && (
          <div className="text-[11px] text-amber-700 inline-flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> View the policy above to enable this checkbox.
          </div>
        )}
        {isSigned && (
          <div className="text-[11px] text-green-700 inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Read{value?.signedAt ? ` on ${new Date(value.signedAt).toLocaleString()}` : ""}.
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div layout className={`mu-card p-5 md:p-6 space-y-5 ${needsReupload ? "border-orange-300 ring-2 ring-orange-100" : ""}`} data-testid={`policy-card-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-[#111] flex items-center gap-2">
            <PenLine className="h-4 w-4 text-[#B60F2A]" /> {item.label}
          </h3>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">{item.description}</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-[#F7F7F7] text-[#6B7280] px-2 py-0.5 rounded">
              To sign: {signerLabel}
            </span>
            {item.optional && (
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded" data-testid={`policy-optional-badge-${item.id}`}>
                Optional for now
              </span>
            )}
          </div>
        </div>
        {needsReupload ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-800 bg-orange-100 ring-1 ring-inset ring-orange-300 px-2 py-1 rounded-full shrink-0" data-testid={`policy-badge-reupload-${item.id}`}>
            <Clock className="h-3.5 w-3.5" /> Re-sign requested
          </span>
        ) : isSigned ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full shrink-0" data-testid={`policy-badge-signed-${item.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Signed
          </span>
        ) : isPending ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 px-2 py-1 rounded-full shrink-0" data-testid={`policy-badge-pending-${item.id}`}>
            <Clock className="h-3.5 w-3.5" /> Digital Signature Pending
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full shrink-0">
            <Clock className="h-3.5 w-3.5" /> Pending
          </span>
        )}
      </div>

      {needsReupload && (
        <div className="rounded-xl bg-orange-50 border border-orange-200 p-3 text-xs text-orange-900" data-testid={`policy-reupload-banner-${item.id}`}>
          <div className="font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> Admin requested this to be sent for sign again — {item.label}</div>
          {(reuploadReason || state.lastActionReason) && <div className="mt-1"><span className="font-semibold">Reason:</span> {reuploadReason || state.lastActionReason}</div>}
          <div className="mt-1 text-orange-800">Review the fields below and click Send for Sign again. Status refreshes automatically.</div>
        </div>
      )}

      {isSigned && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-xs text-green-900" data-testid={`policy-signed-banner-${item.id}`}>
          <div className="font-semibold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Digital signature completed</div>
          <div className="mt-1 text-green-800">
            This form is locked.{value?.signedAt ? ` Signed on ${new Date(value.signedAt).toLocaleString()}.` : ""}
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-5 ${isFrozen ? "opacity-95" : ""}`}>
        <div className="space-y-3">
          <div className="tiny-label">{isFrozen ? "Form locked" : "Fill the form"}</div>
          {(item.fields || []).filter((f) => hasParent2 || (f.key !== "parent2Name" && f.key !== "parent2Phone")).map((f) => (
            <div key={f.key}>
              {f.type === "checkbox" ? (
                <label className={`flex items-start gap-2 text-sm text-[#111] leading-snug select-none ${isFrozen ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}>
                  <input
                    id={`${item.id}-${f.key}`}
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[#B60F2A] cursor-pointer disabled:cursor-not-allowed"
                    checked={!!filled[f.key]}
                    onChange={(e) => setFilled({ [f.key]: e.target.checked })}
                    disabled={isFrozen}
                    data-testid={`${item.id}-${f.key}`}
                  />
                  <span>{f.label}</span>
                </label>
              ) : f.locked || isFrozen ? (
                <>
                  <label className="mu-label" htmlFor={`${item.id}-${f.key}`}>{f.label}</label>
                  <input
                    id={`${item.id}-${f.key}`}
                    readOnly
                    disabled={isFrozen}
                    className="mu-input bg-[#F7F7F7] text-[#6B7280] cursor-not-allowed"
                    value={filled[f.key] || ""}
                    data-testid={`${item.id}-${f.key}`}
                  />
                  {!isFrozen && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{f.lockedHint || "From Candidate Details — edit there to change."}</p>}
                </>
              ) : (
                <>
                  <label className="mu-label" htmlFor={`${item.id}-${f.key}`}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea id={`${item.id}-${f.key}`} rows={2} className="mu-input" value={filled[f.key] || ""} onChange={(e) => setFilled({ [f.key]: e.target.value })} data-testid={`${item.id}-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select id={`${item.id}-${f.key}`} className="mu-input" value={filled[f.key] || ""} onChange={(e) => setFilled({ [f.key]: e.target.value })} data-testid={`${item.id}-${f.key}`}>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input id={`${item.id}-${f.key}`} type={f.type === "date" ? "date" : f.type === "tel" ? "tel" : "text"} inputMode={f.type === "tel" ? "numeric" : undefined} placeholder={f.placeholder} className="mu-input" value={filled[f.key] || ""} onChange={(e) => setFilled({ [f.key]: f.type === "tel" ? e.target.value.replace(/\D+/g, "") : e.target.value })} data-testid={`${item.id}-${f.key}`} />
                  )}
                </>
              )}
            </div>
          ))}
          {!isFrozen && (
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={doPreview} disabled={!canProceed} className="mu-btn-secondary text-sm disabled:opacity-50" data-testid={`policy-preview-generated-${item.id}`}>
              <Eye className="h-4 w-4" /> Preview
            </button>
            <button onClick={openSignModal} disabled={!canProceed || signing} className="mu-btn-primary text-sm disabled:opacity-50" data-testid={`policy-sign-digitally-${item.id}`}>
              <Send className="h-4 w-4" /> {signing ? "Sending…" : "Sign Digitally"}
            </button>
          </div>
          )}
          {!isFrozen && !canProceed && (
            <div className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Fill every field above to enable Preview and Sign Digitally.
            </div>
          )}
          {isPending && !isSigned && (
            <div className="text-[11px] text-orange-700 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Sent for signature — complete signing via the link in your email. This form will lock automatically once signed.
            </div>
          )}
        </div>

        <div className={`rounded-2xl border border-[#E5E7EB] bg-white shadow-inner p-5 text-[13px] leading-relaxed text-[#111] overflow-y-auto ${PREVIEW_PANEL_MAX_HEIGHT[item.id] || "max-h-[440px]"}`} data-testid={`policy-preview-panel-${item.id}`}>
          {previewText()}
        </div>
      </div>

      <Dialog open={signModalOpen} onOpenChange={setSignModalOpen}>
        <DialogContent className="max-w-lg" data-testid={`policy-sign-email-modal-${item.id}`}>
          <DialogHeader>
            <DialogTitle>Send for digital signature</DialogTitle>
            <DialogDescription>
              Signature links will be sent to the addresses below — verified earlier at registration.
            </DialogDescription>
          </DialogHeader>

          {signersLoading ? (
            <div className="py-8 text-center text-sm text-[#6B7280]">Loading recipients…</div>
          ) : (
            <div className="space-y-3">
              {modalSigners.map((s) => (
                <div key={s.role} className="rounded-xl border border-[#E5E7EB] p-4 flex items-center justify-between gap-3" data-testid={`policy-sign-email-${item.id}-${s.role}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#111]">{s.label}</div>
                    <p className="text-[11px] text-[#6B7280]">{sourceHint(s.source)}</p>
                  </div>
                  <div className="text-sm text-[#111] truncate">{s.email || "—"}</div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="mu-btn-secondary"
              onClick={() => setSignModalOpen(false)}
              disabled={signing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mu-btn-primary"
              onClick={confirmSignFromModal}
              disabled={!canSendDigitally || !modalSigners.length}
              data-testid={`policy-confirm-sign-${item.id}`}
            >
              {signing ? "Sending…" : "Send for Digital Sign"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
